<template>
  <v-dialog 
    v-model="viewDialog" 
    persistent 
    max-width="900px"
    transition="dialog-transition"
  >
    <v-card 
      class="premium-card" 
      elevation="24"
      :style="{ background: cardGradient }"
    >
      <!-- Enhanced Header with Glassmorphism Effect -->
      <v-card-title class="premium-header">
        <div class="header-content">
          <div class="title-section">
            <div class="icon-wrapper">
              <v-icon color="#fff" size="28">mdi-account-star</v-icon>
            </div>
            <div class="title-text">
              <h2 class="premium-title">Redeemed Users</h2>
              <p class="subtitle">{{ filteredUsers.length }} users found</p>
            </div>
          </div>
          <v-btn 
            icon 
            class="close-btn"
            color="#fff"
            @click="onClose"
            :ripple="false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text class="premium-content">
        <!-- Enhanced Search Bar with Animation -->
        <div class="search-container">
          <v-text-field
            v-model="search"
            label="Search users by name or email..."
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="comfortable"
            class="premium-search"
            :class="{ 'search-focused': searchFocused }"
            @focus="searchFocused = true"
            @blur="searchFocused = false"
            clearable
            hide-details
          >
            <template v-slot:append-inner>
              <v-fade-transition>
                <v-chip
                  v-if="search"
                  size="small"
                  color="primary"
                  variant="tonal"
                >
                  {{ filteredUsers.length }} results
                </v-chip>
              </v-fade-transition>
            </template>
          </v-text-field>
        </div>

        <!-- Premium Data Table with Enhanced Styling -->
        <div class="table-container">
          <v-data-table
            :headers="headers"
            :items="filteredUsers"
            class="premium-table"
            item-value="_id"
            :items-per-page="10"
            :loading="loading"
            loading-text="Loading users..."
          >
            <!-- Serial Number Column -->
            <template v-slot:[`item.sno`]="{ index }">
              <div class="sno-cell">
                <v-chip
                  size="small"
                  variant="tonal"
                  class="sno-chip"
                >
                  {{ index + 1 }}
                </v-chip>
              </div>
            </template>

            <!-- Name Column with Avatar -->
            <template v-slot:[`item.name`]="{ item }">
              <div class="name-cell">
                <v-avatar size="32" class="mr-4 elevation-2" v-if="item.midThumbnail">
                    <v-img :src="item.midThumbnail" />
                </v-avatar>
                <v-avatar
                  size="32"
                  :color="getAvatarColor(item.name)"
                  class="name-avatar"
                  v-else
                >
                  <span class="text-white font-weight-bold">
                    {{ getInitials(item.name) }}
                  </span>
                </v-avatar>

                <div class="name-info">
                  <div class="name-text">{{ item.name }}</div>
                </div>
              </div>
            </template>

            <!-- Email Column with Icon -->
            <template v-slot:[`item.email`]="{ item }">
              <div class="email-cell">
                <v-icon size="16" color="grey-darken-1" class="mr-2">
                  mdi-email-outline
                </v-icon>
                <span class="email-text">{{ item.email }}</span>
              </div>
            </template>

            <!-- Date Column with Enhanced Formatting -->
            <template v-slot:[`item.reedemed_date`]="{ item }">
              <div class="date-cell">
                <v-chip
                  size="small"
                  variant="outlined"
                  color="info"
                  class="date-chip"
                >
                  <v-icon start size="14">mdi-calendar</v-icon>
                  {{ item.reedemed_date }}
                </v-chip>
              </div>
            </template>

            <!-- Actions Column with Enhanced Checkbox -->
            <template v-slot:[`item.actions`]="{ item }">
              <div class="actions-cell">
                <v-tooltip :text="item.used ? 'Already used' : 'Mark as used'" location="top">
                  <template v-slot:activator="{ props }">
                    <div 
                      v-bind="props"
                      class="checkbox-wrapper"
                      :class="{ 'used': item.used, 'updating': updatingItems.has(item._id) }"
                    >
                      <v-checkbox
                        v-model="item.used"
                        :true-value="true"
                        :false-value="false"
                        :disabled="item.used || updatingItems.has(item._id)"
                        @change="updateUserStatus(item)"
                        color="success"
                        hide-details
                        density="compact"
                      >
                        <template v-slot:label>
                          <v-fade-transition>
                            <v-chip
                              v-if="item.used"
                              size="x-small"
                              color="success"
                              variant="tonal"
                            >
                              <v-icon start size="12">mdi-check</v-icon>
                              Used
                            </v-chip>
                          </v-fade-transition>
                        </template>
                      </v-checkbox>
                      
                      <v-progress-circular
                        v-if="updatingItems.has(item._id)"
                        indeterminate
                        size="16"
                        width="2"
                        color="primary"
                        class="update-spinner"
                      />
                    </div>
                  </template>
                </v-tooltip>
              </div>
            </template>

            <!-- Empty State -->
            <template v-slot:no-data>
              <div class="empty-state">
                <v-icon size="64" color="grey-lighten-2">mdi-account-search</v-icon>
                <h3 class="mt-4 mb-2">No users found</h3>
                <p class="text-grey">Try adjusting your search criteria</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Enhanced Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      color="success"
      variant="tonal"
      location="top"
      class="premium-snackbar"
    >
      <div class="snackbar-content">
        <v-icon start>mdi-check-circle</v-icon>
        {{ successMessage }}
      </div>
      <template v-slot:actions>
        <v-btn
          icon="mdi-close"
          size="small"
          @click="snackbar = false"
        />
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  props: {
    viewDialog: {
      type: Boolean,
      default: false
    },
    couponWinners: {
      type: Array,
      default: () => []
    },
    onClose: {
      type: Function,
      required: true
    },
    item: Object,
    itemId: String
  },
  data() {
    return {
      search: "",
      searchFocused: false,
      loading: false,
      updatingItems: new Set(),
      headers: [
        { 
          text: "#", 
          value: "sno", 
          sortable: false, 
          width: "80px",
          align: "center"
        },
        { 
          text: "User", 
          value: "name",
          width: "200px"
        },
        { 
          text: "Email", 
          value: "email",
          width: "250px"
        },
        { 
          text: "Redeemed Date", 
          value: "reedemed_date",
          width: "160px",
          align: "center"
        },
        { 
          text: "Status", 
          value: "actions", 
          sortable: false,
          width: "120px",
          align: "center"
        }
      ],
      users: [],
      snackbar: false,
      successMessage: "",
      avatarColors: [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
        '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
      ]
    };
  },
  computed: {
    ...mapGetters("awards", {
      reedemedUsers: "getReedemedUsers"
    }),
    filteredUsers() {
      if (!Array.isArray(this.reedemedUsers.redeemedUsers)) return [];

      return this.reedemedUsers.redeemedUsers
        .filter(user => {
          if (!this.search) return true;
          const searchLower = this.search.toLowerCase();
          return (
            user.name.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
          );
        })
        .map((user, index) => ({
          ...user,
          sno: index + 1,
          reedemed_date: new Date(user.reedemed_date).toLocaleDateString("en-US"),
          used: Boolean(user.used)
        }));
    },
    cardGradient() {
      return 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)';
    }
  },
  methods: {
    ...mapActions("awards", {
      getCouponDetails: "getCouponDetails",
      updateCouponCompleted: "updateCouponCompleted"
    }),

    async fetchUsers() {
      const param = { couponId: this.itemId };
      this.loading = true;

      try {
        const response = await this.getCouponDetails(param);
        this.users = response.redeemedUsers.map(user => {
          const date = new Date(user.reedemed_date);
          const formattedDate = `${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${date
            .getDate()
            .toString()
            .padStart(2, "0")}/${date.getFullYear()}`;
          return {
            ...user,
            reedemed_date: formattedDate
          };
        });
      } catch (error) {
        console.error("Error fetching users:", error);
        this.showMessage("Error loading users", "error");
      } finally {
        this.loading = false;
      }
    },

    async updateUserStatus(user) {
      this.updatingItems.add(user._id);
      
      try {
        const payload = {
          award_id: this.itemId,
          used: user.used,
          userId: user._id
        };

        await this.updateCouponCompleted(payload);
        this.showMessage("Status updated successfully!");
        
        // Add a small delay for better UX
        setTimeout(() => {
          this.updatingItems.delete(user._id);
        }, 500);
        
      } catch (error) {
        console.error("Error updating user status:", error);
        this.showMessage("Failed to update status", "error");
        // Revert the change on error
        user.used = !user.used;
        this.updatingItems.delete(user._id);
      }
    },

    showMessage(message,) {
      this.successMessage = message;
      this.snackbar = true;
    },

    getInitials(name) {
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    getAvatarColor(name) {
      const index = name.length % this.avatarColors.length;
      return this.avatarColors[index];
    },

    formatUser(user, index) {
      let formattedDate = "N/A";
      if (user.reedemed_date) {
        const date = new Date(user.reedemed_date);
        if (!isNaN(date.getTime())) {
          formattedDate = `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
            .getDate()
            .toString()
            .padStart(2, "0")}/${date.getFullYear()}`;
        }
      }
      return {
        ...user,
        sno: index + 1,
        reedemed_date: formattedDate
      };
    }
  },
  watch: {
    viewDialog(newVal) {
      if (newVal) {
        this.fetchUsers();
      }
    }
  },
  mounted() {
    this.fetchUsers();
  }
};
</script>

<style scoped>
.premium-card {
  border-radius: 24px !important;
  backdrop-filter: blur(20px);
  /* border: 1px solid rgba(255, 255, 255, 0.2); */
  overflow: hidden;
}

.premium-header {
  background: linear-gradient(135deg,#084468,#1e658d);
  color: white;
  padding: 24px 32px;
  border-radius: 24px 24px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
}

.premium-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 0.875rem;
  opacity: 0.9;
  margin: 4px 0 0 0;
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: rotate(90deg);
}

.premium-content {
  padding: 32px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.search-container {
  margin-bottom: 32px;
  position: relative;
}

.premium-search {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.premium-search.search-focused {
  transform: translateY(-2px);
  filter: drop-shadow(0 8px 25px rgba(102, 126, 234, 0.15));
}

.table-container {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.premium-table {
  background: transparent !important;
}

.premium-table :deep(.v-data-table__wrapper) {
  max-height: 500px;
  border-radius: 20px;
}

.premium-table :deep(th) {
  background: linear-gradient(135deg,#084468,#1e658d) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  border: none !important;
  padding: 16px 12px !important;
}

.premium-table :deep(td) {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.premium-table :deep(tr:hover) {
  background: rgba(102, 126, 234, 0.05) !important;
}

.sno-cell {
  display: flex;
  justify-content: center;
}

.sno-chip {
  font-weight: 600;
  font-size: 0.75rem;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.name-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.name-text {
  font-weight: 600;
  color: #2c3e50;
}

.email-cell {
  display: flex;
  align-items: center;
}

.email-text {
  color: #546e7a;
  font-size: 0.875rem;
}

.date-cell {
  display: flex;
  justify-content: center;
}

.date-chip {
  font-size: 0.75rem;
  font-weight: 500;
}

.actions-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.checkbox-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.checkbox-wrapper.used {
  opacity: 0.7;
}

.checkbox-wrapper.updating {
  opacity: 0.8;
}

.update-spinner {
  position: absolute;
  right: -24px;
  top: 50%;
  transform: translateY(-50%);
}

.empty-state {
  padding: 48px 24px;
  text-align: center;
  color: #757575;
}

.premium-snackbar {
  margin-top: 24px;
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Animations */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.premium-card {
  animation: slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover Effects */
.premium-table :deep(tbody tr) {
  transition: all 0.2s ease;
}

.premium-table :deep(tbody tr:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .premium-header {
    padding: 20px 24px;
  }
  
  .premium-content {
    padding: 24px 20px;
  }
  
  .premium-title {
    font-size: 1.25rem;
  }
  
  .title-section {
    gap: 12px;
  }
}
</style>